// ─────────────────────────────────────────────────────────────────────────────
// BandSwitcher, CD's .band-switcher chip, made functional. Shows the band's
// photo1 avatar (initials fallback), and opens a working dropdown: "View my
// profile", the user's bands (current one marked), and Log out. Mirrors
// profile-wiring.js toggleBandSwitcher/switchToBand.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { User, UserBand } from '../../lib';
import { fetchUserBands, bandPhotoUrl, switchBand, logout } from '../../lib';
import { bandSlug } from '../../lib/contacts';
import { ChevronDown } from './glyphs';
import { MyProfileModal } from './MyProfileModal';
import { ArtistProfileModal } from '../contacts-sections';

export interface BandSwitcherProps {
  readonly user: User | null;
}

/**
 * Role label, with the AV-tier treatment: 'band_member_av' renders as
 * "Band Member" + the CNTRL X mark (/brand/logo-mark.svg), NOT "band member av".
 * Mirrors role-perms.js getRoleDisplayHTML (:448-456). Other roles are
 * underscore-stripped + title-cased.
 */
function RoleLabel({ role }: { role?: string }): JSX.Element {
  const r = String(role || 'artist').toLowerCase();
  if (r === 'band_member_av') {
    return (
      <>
        Band Member
        <img className="role-av-mark" src="/brand/logo-mark.svg" alt="" aria-hidden="true" />
      </>
    );
  }
  return <>{r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</>;
}

export function BandSwitcher({ user }: BandSwitcherProps): JSX.Element {
  const bandName = user?.band_name || '';
  const initial = bandName ? bandName.charAt(0).toUpperCase() : '';
  const photo = bandPhotoUrl(user);
  const currentLc = bandName.toLowerCase();

  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [bandOpen, setBandOpen] = useState(false);
  // Band-primary's identity IS the band → "View my profile" opens the BAND view,
  // not the personal First/Last/skills/travel editor (which is for teammates whose
  // identity travels across bands). Mirrors legacy profile-wiring.js openBand.
  const isBandPrimary = !!(user?.is_band_primary && user?.band_name);
  const [imgBroken, setImgBroken] = useState(false);
  const [bands, setBands] = useState<UserBand[] | null>(null);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Lazy-load the band list the first time the menu opens.
  useEffect(() => {
    if (!open || bands !== null) return;
    setLoading(true);
    fetchUserBands()
      .then((b) => setBands(b))
      .catch(() => setBands([]))
      .finally(() => setLoading(false));
  }, [open, bands]);

  const showImg = !!photo && !imgBroken;

  return (
    <div className="band-switcher-wrap" ref={rootRef}>
      <button
        className="band-switcher"
        type="button"
        aria-label="Switch band"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="band-switcher__avatar">
          {showImg ? <img src={photo} alt="" onError={() => setImgBroken(true)} /> : initial}
        </span>
        <span className="band-switcher__name">{bandName ? bandName.toUpperCase() : 'Your band'}</span>
        <span className="band-switcher__chev">
          <ChevronDown />
        </span>
      </button>

      {open && (
        <div className="band-switcher__dropdown" role="menu">
          <button className="bs-dd__top" type="button" onClick={() => { setOpen(false); if (isBandPrimary) setBandOpen(true); else setProfileOpen(true); }}>
            <span className="bs-dd__top-label">View my profile</span>
            <span className="bs-dd__top-meta">{user?.email || ''}</span>
          </button>

          <div className="bs-dd__divider" />
          <div className="bs-dd__head">Your bands</div>

          {loading && <div className="bs-dd__muted">Loading bands…</div>}
          {!loading && bands && bands.length === 0 && (
            <div className="bs-dd__muted">No band memberships yet.</div>
          )}
          {!loading && bands && bands.length > 0 && (
            <ul className="bs-dd__list">
              {bands.map((b) => {
                const isCurrent = String(b.band_name || '').toLowerCase() === currentLc;
                return (
                  <li key={b.band_name}>
                    <button
                      className={'bs-dd__row' + (isCurrent ? ' is-current' : '')}
                      type="button"
                      onClick={() => (isCurrent ? setOpen(false) : void switchBand(b.band_name))}
                    >
                      <span className="bs-dd__row-name">{b.band_name}</span>
                      <span className="bs-dd__row-role">
                        <RoleLabel role={b.role} />
                      </span>
                      {isCurrent && <span className="bs-dd__current">CURRENT</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="bs-dd__divider" />
          <button className="bs-dd__logout" type="button" onClick={() => logout()}>
            Log out
          </button>
        </div>
      )}

      {profileOpen && <MyProfileModal onClose={() => setProfileOpen(false)} />}
      {bandOpen && (
        <ArtistProfileModal
          slug={bandSlug(bandName)}
          bandName={bandName}
          photoUrl={photo}
          selfView
          onClose={() => setBandOpen(false)}
          onBack={() => setBandOpen(false)}
          onOpenMember={() => { /* no person editor from the dashboard band view */ }}
        />
      )}
    </div>
  );
}
